import React, { useState, useRef, useEffect } from 'react-x';
import propTypes from 'prop-types-x';
import classNames from 'classnames';
import _isFunction from 'lodash/isFunction.js';
import { useUpdateEffect } from '../hooks/useUpdateEffect.js';
import { CssAnimation } from './CssAnimation.jsx';
import { GalleryGrid } from './GalleryGrid.jsx';

const initState = (images, selected) => images.map((image, index) => Object.assign({}, image, { selected : selected.indexOf(index) > -1 }));

const multiSelectUpdateState = (images, updateIndex) => images.map((image, index) => index === updateIndex ? (Object.assign({}, image, { selected : !image.selected })) : image);

const singleSelectUpdateState = (images, updateIndex) => images.map((image, index) => index === updateIndex ? (Object.assign({}, image, { selected : !image.selected })) : (Object.assign({}, image, { selected : false })));

export function ImageSelectionGrid(props) {

  const { 
    images, 
    loading,
    maxSelection, 
    onSelectCallback, 
    initSelected 
  } = props;

  const [imageState, setImageState] = useState(initState(images, initSelected));
  const selectedImageSrcsRef = useRef([]);
  const selectedImagesRef = useRef([]);
  const block = 'c-image-selection-grid';

  function toggleSelection(image, index) {   
    const imageObj = JSON.parse(image.dataset.image);
    const imageSrc = imageObj.base || imageObj.url || imageObj.src;
    const imageIndex = selectedImageSrcsRef.current.indexOf(imageSrc);
    const imageData = imageObj.data || {};

    if (imageIndex === -1) { // Image does not exist in array
      if (maxSelection > 1) { // multi select
        if (selectedImagesRef.current.length !== maxSelection) {

          selectedImageSrcsRef.current.push(imageSrc);
          selectedImagesRef.current.push({
            url: imageSrc,
            data: imageData,
            index
          });

          setImageState(prevState => multiSelectUpdateState(prevState, index));     
        } 
      }
      else { // single select
        selectedImageSrcsRef.current = [imageSrc];
        selectedImagesRef.current =[{
          url: imageSrc,
          data: imageData,
          index
        }];

        setImageState(prevState => singleSelectUpdateState(prevState, index)); 
      }
    } 
    else {
      selectedImageSrcsRef.current.splice(imageIndex, 1);
      selectedImagesRef.current.splice(imageIndex, 1);
      setImageState(prevState => multiSelectUpdateState(prevState, index));
    }
  };

  useEffect(() => {
    // If should load with preselected images add them to the ref arrays
    if (initSelected?.length) {
      initSelected.forEach(index => {
        const image = images[index];
        const imageSrc = image.base || image.url || image.src;
        selectedImageSrcsRef.current.push(imageSrc);
        selectedImagesRef.current.push({
          url: imageSrc,
          data: image.data || {},
          index
        });
      })
    }
  }, []);

  useUpdateEffect(() => {
    if (_isFunction(onSelectCallback)) {
      onSelectCallback(selectedImagesRef.current);
    }
  }, [imageState]);

  const selectImageOverlay = image => (
    <CssAnimation
    enter={{ name: 'fade-in', duration: 0.5, delay: `0.75 hidden` }}
    triggerEnter={true}
    >
      <div className={classNames({
        [`${block}__image-overlay`]: true,
        [`${block}__image-overlay--active`]: image.selected
      })}>
        <div className={classNames({
          [`${block}__selected-status-icon`]: true,
          [`${block}__selected-status-icon--active`]: image.selected
          })}></div>
      </div>
    </CssAnimation>
  );

  return (
    <div className={block}>
      <GalleryGrid 
      images={imageState} 
      loading={loading}
      blockModifier='fixed-3-col'
      useImageAspectRatio={false}
      imageOverlay={selectImageOverlay}
      onClickCallback={toggleSelection}
      />
    </div>
  );
};

ImageSelectionGrid.propTypes = {
  images: propTypes.array,
  loading: propTypes.string,
  maxSelection: propTypes.number,
  onSelectCallback: propTypes.func,
  initSelected: propTypes.array
};

ImageSelectionGrid.defaultProps = {
  loading: 'lazy',
  initSelected: []
};
